'use client'

import { cn } from '@brew/ui/cn'
import { useAuth, useClerk } from '@clerk/nextjs'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useMemo, useRef, useState, useTransition } from 'react'
import {
  getPlanChangeConfirmCopy,
  type PlanChangeKind,
} from '@/components/billing/plan-change-confirm-copy'
import { useAppLocale } from '@/components/i18n/locale-provider'
import { BrewSummary, BrewSummaryRow } from '@/components/ui/brew-dialog'
import {
  AlertTriangleIcon,
  ArrowDownIcon,
  CheckIcon,
  type LucideIcon,
  PlayIcon,
  ZapIcon,
} from '@/components/ui/brew-dialog/icons'
import { useConfirm } from '@/components/ui/confirm-dialog'
import {
  type PricingButtonVariant,
  PricingCard,
  type PricingCardBadge,
} from '@/components/ui/pricing-card'
import { ENTERPRISE_CONTACT_URL } from '@/components/upgrade/upgrade-limit-config'
import {
  cancelSubscriptionAction,
  changePlanAction,
  openBillingPortalAction,
  reactivateSubscriptionAction,
} from '@/lib/actions/billing'
import type { BillingTranslator } from '@/lib/billing/billing-translator'
import {
  BILLING_PLANS,
  type BillingPlanKey,
  describePlanFeatures,
  getVisiblePlanKeysFor,
  isSelfServePlanKey,
  isSunsetPlanKey,
  type SelfServePlanKey,
} from '@/lib/billing/plans'
import {
  defaultPriceSummary,
  formatCurrency,
} from '@/lib/billing/price-summary'
import {
  type BillingCadence,
  describePromoPriceLabels,
  GROWTH_PROMO,
  getChargedPriceCents,
  isPromoEligible,
} from '@/lib/billing/promo'
import type { BillingOverview, BillingPriceSummary } from '@/lib/billing/types'
import {
  type BillingPlanActionIntent,
  getBillingPlanActionState,
} from '@/lib/billing/ui-state'
import { dateTimeFormat } from '@/lib/i18n/format'
import { observeProductRequest } from '@/lib/observability/product-request'
import { getErrorMessage } from '@/lib/shared/errors/error-message'
import { appToast } from '@/lib/shared/toast'
import {
  startTrackedCheckout,
  trackInAppPlanUpgrade,
  trackInAppPlanUpgradeStarted,
} from '@/lib/site/checkout-tracking'

const DEFAULT_PRICE_SUMMARIES = defaultPriceSummary()

/** The confirm's medal glyph per plan change. */
const PLAN_CHANGE_ICON: Record<PlanChangeKind, LucideIcon> = {
  upgrade: ZapIcon,
  downgrade: ArrowDownIcon,
  cancel: AlertTriangleIcon,
  resume: PlayIcon,
  keep: CheckIcon,
}

type PlanGridProps = {
  billingOverview?: BillingOverview | null
  priceSummaries: BillingPriceSummary
  className?: string
  showEnterprise?: boolean
  /** Members see the plans but cannot start checkout or change tiers. */
  readOnly?: boolean
  /**
   * Where the Enterprise card leads instead of the booking link ("Build
   * your plan"). The public pricing page sends readers to /enterprise to
   * build a plan; the in-app plan page keeps the call.
   */
  enterpriseHref?: string
}

/** A plan's monthly price, in the reader's `locale`; Enterprise's is custom. */
function getPlanPriceLabel(args: {
  t: BillingTranslator
  locale: string
  planKey: BillingPlanKey
  priceSummaries: BillingPriceSummary
}) {
  if (args.planKey === 'free') {
    return formatCurrency({ amount: 0, currency: 'USD', locale: args.locale })
  }

  if (args.planKey === 'enterprise') {
    return args.t('planGrid.customPrice')
  }

  const selfServePlanKey = args.planKey as SelfServePlanKey
  const price =
    args.priceSummaries[selfServePlanKey] ??
    DEFAULT_PRICE_SUMMARIES[selfServePlanKey]!
  return formatCurrency({
    amount: price.amount,
    currency: price.currency,
    locale: args.locale,
  })
}

const EFFECTIVE_DATE: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
}

function formatEffectiveDate(
  ms: number | null | undefined,
  locale: string
): string | null {
  if (typeof ms !== 'number') {
    return null
  }
  return dateTimeFormat(locale, EFFECTIVE_DATE).format(ms)
}

export function PlanGrid({
  billingOverview,
  priceSummaries,
  className,
  showEnterprise = true,
  readOnly = false,
  enterpriseHref,
}: PlanGridProps) {
  const t = useTranslations('billing')
  const tCommon = useTranslations('common')
  const locale = useAppLocale()
  const gridRef = useRef<HTMLDivElement | null>(null)
  // Hydration-stable clock for the prepaid-term time bound (repo pattern:
  // app/(app)/plan/plan-content.tsx).
  const nowMsRef = useRef(Date.now())
  const { isLoaded, isSignedIn, userId, orgId } = useAuth()
  const { openSignUp } = useClerk()
  const router = useRouter()
  const [pendingPlanKey, setPendingPlanKey] = useState<BillingPlanKey | null>(
    null
  )
  const [isPending, startTransition] = useTransition()
  const confirmPlanChange = useConfirm()

  const currentPlanKey = billingOverview?.planKey ?? 'free'

  // Clerk resolves on the client after hydration. Until it does, a billing
  // overview (only ever loaded for a signed-in org member) is the better
  // signal, so a paying org's current card never flashes a sign-up CTA.
  const hasBillingOverview =
    billingOverview !== null && billingOverview !== undefined
  const viewerIsSignedIn = isLoaded ? isSignedIn === true : hasBillingOverview
  const viewerHasOrganization = isLoaded ? Boolean(orgId) : hasBillingOverview

  // Sunset tiers render ONLY for the viewer already on one (their "Legacy"
  // card), so the visible set depends on who is looking — it is not a static
  // slice of BILLING_PLAN_ORDER.
  const orderedPlanKeys = useMemo(
    () =>
      getVisiblePlanKeysFor({
        currentPlanKey,
        includeEnterprise: showEnterprise,
      }),
    [currentPlanKey, showEnterprise]
  )

  // Promo visibility. SIGNED-IN ONLY, and only with a real billing overview in
  // hand — this grid renders on the PUBLIC /pricing page too, and the offer is
  // for signed-up orgs that have never paid, not for anonymous visitors.
  //
  // Both excluded cases fail CLOSED on purpose:
  //   - signed OUT: eligibility is unknowable (there is no org yet), and the
  //     public page must advertise the plain $99/mo price it can actually
  //     honour for anyone who clicks.
  //   - signed IN with NO overview (Clerk still resolving, or the Convex read
  //     failed): unknown, so hide. Showing a discount to an org that may
  //     already be paying is worse than an eligible org waiting a beat for it.
  //
  // The server re-derives eligibility at checkout regardless, so this controls
  // what is DISPLAYED only — nobody can be charged the wrong amount.
  const isPromoVisible =
    isSignedIn === true &&
    billingOverview !== null &&
    billingOverview !== undefined &&
    isPromoEligible({
      planKey: currentPlanKey,
      hasEverPaid: billingOverview.hasEverPaid,
    })

  // A plan's name is a product name, the same in every language.
  const currentPlanName = billingOverview?.planName ?? BILLING_PLANS.free.name
  const effectiveDateLabel = formatEffectiveDate(
    billingOverview?.currentPeriodEnd,
    locale
  )
  // A scheduled downgrade shows the still-entitled (higher) tier as current;
  // surface a "Keep" affordance on it and a "Scheduled" label on the target.
  const hasScheduledDowngrade =
    (billingOverview?.hasScheduledPlanChange ?? false) &&
    billingOverview?.scheduledPlanChangeDirection === 'downgrade'
  const scheduledPlanKey = billingOverview?.nextPlanKey ?? null

  const startCheckout = (
    planKey: SelfServePlanKey,
    cadence: BillingCadence = 'monthly'
  ) => {
    startTransition(async () => {
      setPendingPlanKey(planKey)
      try {
        const planPrice = priceSummaries[planKey]
        const planName = BILLING_PLANS[planKey].name
        if (!planPrice) {
          appToast.error(t('planGrid.checkoutFailed'))
          return
        }

        // Report what Stripe will actually charge, so `brew_checkout_started`
        // matches the session's `amount_total` — the WHOLE TERM for a
        // commitment. The server independently re-derives eligibility; this
        // value is analytics only.
        const chargedPriceCents = getChargedPriceCents(cadence, planPrice)

        const result = await startTrackedCheckout({
          planKey,
          planName,
          planPriceCents: chargedPriceCents,
          planPriceCurrency: planPrice.currency,
          cadence,
          userId,
          orgId,
          currentPlanName: billingOverview?.planName,
        })

        if (!result.ok) {
          appToast.error(result.error)
          return
        }

        window.location.href = result.url
      } finally {
        setPendingPlanKey(null)
      }
    })
  }

  const openPortal = (planKey: BillingPlanKey) => {
    startTransition(async () => {
      setPendingPlanKey(planKey)
      try {
        const result = await observeProductRequest(
          'billing.portal_session',
          openBillingPortalAction,
          {
            context: { orgId: orgId ?? undefined, userId: userId ?? undefined },
            resource: planKey,
          }
        )
        window.location.href = result.url
      } catch (error) {
        appToast.error(getErrorMessage(error, t('portalFailed')))
      } finally {
        setPendingPlanKey(null)
      }
    })
  }

  // The grid is a thin dispatcher — `getBillingPlanActionState` decides the
  // intent, this routes it. Reprice/cancel/resume go through the confirm
  // dialog (money-affecting); checkout/manage navigate to Stripe.
  const handlePlanSelect = (
    planKey: BillingPlanKey,
    intent: BillingPlanActionIntent
  ) => {
    switch (intent) {
      case 'signup':
        openSignUp()
        return
      case 'select-org':
        router.push('/create-organization')
        return
      case 'contact':
        window.open(ENTERPRISE_CONTACT_URL, '_blank', 'noopener,noreferrer')
        return
      case 'checkout':
        if (planKey === 'free' || planKey === 'enterprise') {
          return
        }
        startCheckout(planKey)
        return
      case 'manage':
        openPortal(planKey)
        return
      case 'upgrade':
      case 'downgrade':
      case 'cancel':
      case 'resume':
      case 'keep':
        requestPlanChange(intent, planKey)
        return
      case 'none':
        return
    }
  }

  // The app-wide confirm holds itself open with both buttons disabled while
  // `runPlanChange` settles, so a slow Stripe call cannot be double-submitted;
  // a failure toasts inside the action and the dialog then closes.
  const requestPlanChange = (kind: PlanChangeKind, planKey: BillingPlanKey) => {
    const copy = getPlanChangeConfirmCopy(t, {
      kind,
      currentPlanName,
      targetPlanName: BILLING_PLANS[planKey].name,
      targetPriceLabel:
        kind === 'upgrade'
          ? t('perMonth', {
              price: getPlanPriceLabel({ t, locale, planKey, priceSummaries }),
            })
          : null,
      effectiveDateLabel,
    })
    void confirmPlanChange({
      icon: PLAN_CHANGE_ICON[kind],
      title: copy.title,
      description: copy.body,
      body: copy.summary ? (
        <BrewSummary>
          {copy.summary.map((row) => (
            <BrewSummaryRow key={row.label} label={row.label}>
              <span className={cn({ 'font-semibold': row.isStrong })}>
                {row.value}
              </span>
            </BrewSummaryRow>
          ))}
        </BrewSummary>
      ) : undefined,
      confirmLabel: copy.confirmLabel,
      cancelLabel: copy.cancelLabel,
      destructive: copy.confirmVariant === 'destructive',
      pendingLabel: t('planGrid.working'),
      onConfirm: () => runPlanChange(kind, planKey),
    })
  }

  const runPlanChange = async (
    kind: PlanChangeKind,
    planKey: BillingPlanKey
  ) => {
    try {
      if (kind === 'upgrade' || kind === 'downgrade' || kind === 'keep') {
        if (!isSelfServePlanKey(planKey)) {
          appToast.error(t('planGrid.unsupportedChange'))
          return
        }
        const price =
          priceSummaries[planKey] ?? DEFAULT_PRICE_SUMMARIES[planKey]
        if (kind === 'upgrade') {
          void trackInAppPlanUpgradeStarted({
            userId,
            orgId,
            priorPlanName: currentPlanName,
            planKey,
            planName: BILLING_PLANS[planKey].name,
            price,
          })
        }
        const result = await observeProductRequest(
          'billing.change_plan',
          () => changePlanAction(planKey),
          {
            context: { orgId: orgId ?? undefined, userId: userId ?? undefined },
            resource: planKey,
          }
        )
        const planName = BILLING_PLANS[planKey].name
        switch (result.direction) {
          case 'none':
            appToast.warning(t('planGrid.alreadyOn', { plan: planName }))
            break
          case 'kept':
            appToast.success(t('planGrid.stayingOn', { plan: planName }))
            break
          case 'downgrade':
            appToast.success(t('planGrid.startsNextCycle', { plan: planName }))
            break
          case 'upgrade':
            appToast.success(t('planGrid.upgradedTo', { plan: planName }))
            trackInAppPlanUpgrade({
              direction: result.direction,
              userId,
              orgId,
              priorPlanName: currentPlanName,
              planKey,
              planName,
              price,
              conversion: result.upgradeConversion,
            })
            break
        }
      } else if (kind === 'cancel') {
        const result = await observeProductRequest(
          'billing.cancel',
          cancelSubscriptionAction,
          {
            context: { orgId: orgId ?? undefined, userId: userId ?? undefined },
          }
        )
        appToast.success(
          result.alreadyScheduled
            ? t('planGrid.alreadyCancelling')
            : t('planGrid.cancelsAtPeriodEnd')
        )
      } else {
        await observeProductRequest(
          'billing.reactivate',
          reactivateSubscriptionAction,
          {
            context: { orgId: orgId ?? undefined, userId: userId ?? undefined },
          }
        )
        appToast.success(t('planGrid.keepsRenewing'))
      }
    } catch (error) {
      appToast.error(getErrorMessage(error, tCommon('errors.generic')))
    }
  }

  return (
    <div
      ref={gridRef}
      data-plan-grid
      className={cn(
        'grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3',
        // The xl column count must track the VISIBLE card count, which is now
        // viewer-dependent: only a grandfathered org still sees 5 (their
        // Legacy card). Hardcoding 5 left everyone else with an empty
        // trailing column. Tailwind needs static class names, so this is a
        // lookup rather than an interpolation. Stay on grid at every
        // breakpoint — `2xl:flex` left the cards content-sized and opened a
        // gap beside the last card.
        {
          'xl:grid-cols-5': orderedPlanKeys.length >= 5,
          'xl:grid-cols-4': orderedPlanKeys.length === 4,
          'xl:grid-cols-3': orderedPlanKeys.length <= 3,
        },
        className
      )}
    >
      {orderedPlanKeys.map((planKey) => {
        const plan = BILLING_PLANS[planKey]
        const isCurrentPlan = billingOverview?.planKey === planKey
        const features = describePlanFeatures(t, planKey, locale)

        // The offer attaches to exactly one card. For an eligible org the
        // prepaid term is the featured action (Starter's replacement) and
        // month-to-month stays available as the quieter alternative. The
        // two amounts stay on two controls so a buyer cannot confuse them.
        const hasPromo = isPromoVisible && planKey === GROWTH_PROMO.planKey
        const promoPrice = hasPromo
          ? (priceSummaries[GROWTH_PROMO.planKey] ??
            DEFAULT_PRICE_SUMMARIES[GROWTH_PROMO.planKey])
          : null
        const promoLabels = promoPrice
          ? describePromoPriceLabels(t, promoPrice, locale)
          : null

        const actionState = getBillingPlanActionState(t, {
          planKey,
          currentPlanKey,
          isSignedIn: viewerIsSignedIn,
          hasOrganization: viewerHasOrganization,
          portalEligible: billingOverview?.portalEligible ?? false,
          cancelAtPeriodEnd: billingOverview?.cancelAtPeriodEnd ?? false,
          cancelAt: billingOverview?.cancelAt,
          hasScheduledDowngrade,
          scheduledPlanKey,
          status: billingOverview?.status,
          isGrowthCommitment: billingOverview?.isGrowthCommitment,
          currentPeriodEnd: billingOverview?.currentPeriodEnd,
          nowMs: nowMsRef.current,
        })
        const isPromoCheckout =
          promoLabels !== null && actionState.intent === 'checkout'
        // The promo card's primary CTA buys the PREPAID term; every other
        // state dispatches on the computed intent.
        const handleCardSelect = () => {
          if (readOnly) {
            return
          }
          if (isPromoCheckout) {
            startCheckout(planKey as SelfServePlanKey, 'commit')
            return
          }
          handlePlanSelect(planKey, actionState.intent)
        }

        const price = isPromoCheckout
          ? promoLabels.discountedLabel
          : getPlanPriceLabel({ t, locale, planKey, priceSummaries })

        // The public pricing page links Enterprise to /enterprise instead of
        // opening the booking link.
        const enterpriseLink =
          actionState.intent === 'contact' ? enterpriseHref : undefined
        let buttonText: string = actionState.buttonText
        if (isPromoCheckout) {
          buttonText = promoLabels.commitCtaLabel
        } else if (enterpriseLink !== undefined) {
          buttonText = t('planGrid.buildYourPlan')
        }
        const buttonVariant: PricingButtonVariant = actionState.buttonVariant
        const isDisabled = readOnly || actionState.disabled
        let badge: PricingCardBadge | undefined
        // Keyed on the SAME predicate as the discounted price/CTA — keying
        // on promoLabels alone can flash a "50% off" ribbon next to a $99
        // price while orgId is transiently null (intent not yet 'checkout').
        if (isPromoCheckout && promoLabels) {
          badge = { label: promoLabels.ribbonLabel, tone: 'promo' }
        } else if (isSunsetPlanKey(planKey)) {
          badge = { label: t('planGrid.legacy'), tone: 'legacy' }
        }

        return (
          <PricingCard
            key={plan.key}
            name={plan.name}
            price={price}
            compareAtPrice={isPromoCheckout ? promoLabels.listLabel : undefined}
            secondaryCta={
              isPromoCheckout
                ? {
                    label: promoLabels.monthlyCtaLabel,
                    onSelect: () =>
                      startCheckout(planKey as SelfServePlanKey, 'monthly'),
                    loading: isPending && pendingPlanKey === planKey,
                  }
                : undefined
            }
            onSelect={handleCardSelect}
            badge={badge}
            features={[...features]}
            isPopular={plan.key === 'growth'}
            isCurrentPlan={isCurrentPlan}
            isCustomPricing={plan.key === 'enterprise'}
            buttonText={buttonText}
            buttonHref={enterpriseLink}
            buttonVariant={buttonVariant}
            currentButtonHoverText={actionState.currentButtonHoverText}
            disabled={isDisabled}
            buttonLoading={isPending && pendingPlanKey === planKey}
          />
        )
      })}
    </div>
  )
}
